import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHome,
  faUsers,
  faShop,
  faTruck,
  faShip,
  faClipboardList,
  faCreditCard,
  faSignOutAlt,
  faTimes,
  faBell,
  faBox,
} from "@fortawesome/free-solid-svg-icons";
import { useUser } from "../hooks/useUser";

interface SidebarProps {
  expanded: boolean;
  isMobile: boolean;
  toggleSidebar: () => void;
  currentPath: string;
}

const Sidebar: React.FC<SidebarProps> = ({
  expanded,
  isMobile,
  toggleSidebar,
  currentPath,
}) => {
  const navigate = useNavigate();
  const { logout } = useUser();

  // Prevent body scrolling when sidebar is open on mobile
  useEffect(() => {
    if (isMobile && expanded) {
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.width = "100%";
    } else {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
    }

    return () => {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
    };
  }, [isMobile, expanded]);

  const menuItems = [
    { name: "Dashboard", icon: faHome, path: "/dashboard" },
    { name: "Users", icon: faUsers, path: "/users" },
    { name: "Notifications", icon: faBell, path: "/notifications" },
    { name: "Products", icon: faBox, path: "/products" },
    { name: "Product Owners", icon: faShop, path: "/product-owners" },
    { name: "Vehicles", icon: faTruck, path: "/vehicles" },
    { name: "Transports", icon: faShip, path: "/transports" },
    { name: "Orders", icon: faClipboardList, path: "/orders" },
    { name: "Payment", icon: faCreditCard, path: "/payment" },
  ];

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <>
      {/* Mobile overlay when sidebar is open */}
      {isMobile && expanded && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300"
          onClick={toggleSidebar}
        />
      )}

      <div className="relative">
        {/* Sidebar with close button inside */}
        <aside
          className={`bg-[#3c0b0b] text-white ${
            expanded ? "w-64" : "w-20"
          } transition-all max-h-svh duration-300 ease-in-out flex flex-col fixed top-0 bottom-0 left-0 md:relative md:top-auto md:bottom-auto md:left-auto z-50 ${
            isMobile && !expanded ? "-translate-x-full" : "translate-x-0"
          }`}
          onClick={(e) => e.stopPropagation()}
          style={{
            height: isMobile
              ? "calc(100vh - env(safe-area-inset-top))"
              : "100%",
          }}
        >
          {/* Close button for mobile - positioned inside sidebar header */}
          {isMobile && expanded && (
            <button
              className="absolute top-4 -right-16 bg-white rounded-full w-10 h-10 flex items-center justify-center shadow-lg hover:bg-gray-100 transition-colors duration-200 z-10"
              onClick={toggleSidebar}
            >
              <FontAwesomeIcon
                icon={faTimes}
                className="text-gray-800 text-sm"
              />
            </button>
          )}

          <div className="p-4 flex justify-center items-center">
            <div
              className={`${
                expanded ? "h-24 w-full" : "h-12 w-12"
              } relative bg-white rounded-md p-1 transition-all duration-300`}
            >
              <img
                src="/images/logo.png"
                alt="Logo"
                className="w-full h-full object-contain"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            </div>
          </div>

          {/* Scrollable content area */}
          <div className="flex-grow overflow-y-auto pb-4">
            <nav className="mt-8">
              <ul>
                {menuItems.map((item) => (
                  <li key={item.name} className="mb-2 px-4">
                    <Link to={item.path}>
                      <div
                        className={`flex items-center py-3 px-4 rounded-lg transition-all duration-200 ease-in-out
                          ${
                            currentPath === item.path
                              ? "bg-white text-[#3c0b0b] shadow-md"
                              : "hover:bg-[#4c1b1b]"
                          }`}
                      >
                        <FontAwesomeIcon
                          icon={item.icon}
                          className={`${expanded ? "mr-4" : "mx-auto"}`}
                          size="lg"
                        />
                        {expanded && (
                          <span className="font-medium">{item.name}</span>
                        )}
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          </div>

          {/* Sign out button - always visible at bottom */}
          <div className="p-4 border-t border-[#4c1b1b] bg-[#3c0b0b]">
            <button
              onClick={handleLogout}
              className="flex items-center w-full py-3 px-4 hover:bg-[#4c1b1b] rounded-lg transition-all duration-200 ease-in-out"
            >
              <FontAwesomeIcon
                icon={faSignOutAlt}
                className={`${expanded ? "mr-4" : "mx-auto"}`}
                size="lg"
              />
              {expanded && <span className="font-medium">Sign Out</span>}
            </button>
          </div>
        </aside>
      </div>
    </>
  );
};

export default Sidebar;
